import { Check, Minus, HelpCircle } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type FeatureValue = boolean | "partial" | string;

interface ComparisonRow {
  feature: string;
  helper?: string;
  qcard: FeatureValue;
  popl: FeatureValue;
  blinq: FeatureValue;
}

const comparisonData: ComparisonRow[] = [
  // === Core Contact Sharing (All Platforms) ===
  {
    feature: "Share contact information digitally",
    qcard: true,
    popl: true,
    blinq: true,
  },
  {
    feature: "QR code sharing (including offline)",
    qcard: true,
    popl: true,
    blinq: true,
  },
  {
    feature: "Bio/Professional summary",
    qcard: true,
    popl: true,
    blinq: "Headline only",
  },
  // === Profile Depth & Customization (Limited in Popl & Blinq) ===
  {
    feature: "Rich custom sections (links with image + description)",
    helper: "Showcase services, resources, or content beyond basic links",
    qcard: true,
    popl: "Limited",
    blinq: "Limited",
  },
  {
    feature: "Unlimited links & content",
    qcard: true,
    popl: "Limited",
    blinq: "Limited",
  },
  // === Discoverability & Engagement (QCard-Focused) ===
  {
    feature: "Keywords/tags for contact discoverability",
    helper: "Help contacts find you later when searching their phone",
    qcard: true,
    popl: false,
    blinq: false,
  },
  {
    feature: "Action buttons (purpose-built CTAs)",
    helper: "Dedicated action buttons to book meetings, visit site, or contact",
    qcard: true,
    popl: false,
    blinq: false,
  },
  // === Team & Organization Features (QCard Only) ===
  {
    feature: "Team directory from one profile",
    helper: "Access multiple team members' cards from a single scan",
    qcard: true,
    popl: false,
    blinq: false,
  },
  // === Brand Presence Across Touchpoints ===
  {
    feature: "Downloadable branded assets",
    helper: "Broad set of branded assets across contexts",
    qcard: true,
    popl: "Limited",
    blinq: "Limited",
  },
  // === Overall Profile Experience ===
  {
    feature: "Rich, story-driven professional profile",
    helper: "Create a compelling narrative, not just a list of links",
    qcard: true,
    popl: false,
    blinq: false,
  },
];

const FeatureIndicator = ({ value }: { value: FeatureValue }) => {
  if (value === true) {
    return (
      <div className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center mx-auto">
        <Check className="w-4 h-4 text-primary" />
      </div>
    );
  }
  if (value === false) {
    return (
      <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center mx-auto">
        <Minus className="w-4 h-4 text-muted-foreground/50" />
      </div>
    );
  }
  return (
    <span className="text-sm text-muted-foreground text-center block">{value}</span>
  );
};

const ComparisonGridSection = () => {
  return (
    <section className="py-24 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-4">
            Free Version Comparison
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            More Than Just{" "}
            <span className="text-gradient">Contact Sharing</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            See how QCard's free version helps professionals stand out, be remembered, and drive meaningful next steps.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-card rounded-2xl border border-border overflow-hidden shadow-lg">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-border bg-muted/50">
                  <TableHead className="w-[40%] py-5 px-6 text-foreground font-semibold">
                    Capability
                  </TableHead>
                  <TableHead className="w-[20%] text-center py-5 px-4">
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-primary font-bold text-lg">QCard</span>
                      <span className="text-xs text-muted-foreground font-normal">Free</span>
                    </div>
                  </TableHead>
                  <TableHead className="w-[20%] text-center py-5 px-4">
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-foreground font-medium">Popl</span>
                      <span className="text-xs text-muted-foreground font-normal">Free</span>
                    </div>
                  </TableHead>
                  <TableHead className="w-[20%] text-center py-5 px-4">
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-foreground font-medium">Blinq</span>
                      <span className="text-xs text-muted-foreground font-normal">Free</span>
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {comparisonData.map((row, index) => (
                  <TableRow 
                    key={index} 
                    className="border-b border-border/50 last:border-0 hover:bg-muted/30 transition-colors"
                  >
                    <TableCell className="py-4 px-6">
                      <div className="flex items-start gap-2">
                        <span className="text-sm text-foreground font-medium">
                          {row.feature}
                        </span>
                        {row.helper && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <HelpCircle className="w-4 h-4 text-muted-foreground/60 flex-shrink-0 mt-0.5 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs">
                              <p className="text-xs">{row.helper}</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-4 px-4 bg-primary/5">
                      <FeatureIndicator value={row.qcard} />
                    </TableCell>
                    <TableCell className="py-4 px-4">
                      <FeatureIndicator value={row.popl} />
                    </TableCell>
                    <TableCell className="py-4 px-4">
                      <FeatureIndicator value={row.blinq} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Comparison based on publicly available free-tier functionality as of 2025.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ComparisonGridSection;
